// ======================================================================
// 📁 File: BOQCreateOverlay.jsx
// Path:
// src/modules/ppe/pages/engineering/boq/BOQCreateOverlay.jsx
// ======================================================================
//
// PURPOSE:
//   - Create / Edit / View BOQ Master
//   - UI identical to RoutingCreateOverlay.jsx
//   - Works ONLY with PPE BOQ MASTER tables
//
// IMPORTANT:
//   - Does NOT touch Order BOQ
//   - Does NOT create versions manually
//   - Backend controls approval lifecycle
// ======================================================================

import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

import axiosClient from "../../../../../api/axiosClient";

import BOQHeaderForm from "./BOQHeaderForm";
import BOQLineTable from "./BOQLineTable";

export default function BOQCreateOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { boqId } = useParams();

  // ------------------------------------------------------------
  // MODE DETECTION
  // ------------------------------------------------------------
  const isView = location.pathname.includes("/view/");
  const isEdit = Boolean(boqId) && !isView;

  const mode = isView
    ? "view"
    : isEdit
    ? "edit"
    : "create";

  // ------------------------------------------------------------
  // STATE
  // ------------------------------------------------------------
  const [header, setHeader] = useState({
    boq_name: "",
    product_family_id: "",
    product_variant_id: "",
    equipment_id: "",   
    remarks: "",
  });

  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------
  // CLOSE OVERLAY
  // ------------------------------------------------------------
  const closeOverlay = () => {
    navigate("/ppe/engineering/boq");
  };

  // ------------------------------------------------------------
  // LOAD BOQ MASTER (EDIT / VIEW)
  // ------------------------------------------------------------
  useEffect(() => {
    if (!boqId) return;

    setLoading(true);

    axiosClient
      .get(`/api/ppe/orders/boq-masters/${boqId}`)
      .then((res) => {
        const data = res.data?.data;
        if (!data) return;

        setHeader({
          boq_name: data.header.boq_name,
          product_family_id: data.header.product_family_id,
          product_variant_id: data.header.product_variant_id || "",
          equipment_id: data.header.equipment_id || "",  
          remarks: data.header.remarks || "",
        });

        // ============================================================
        // ✅ 2026-02-19 FIX
        // Use backend rows directly — prevents loss of derived fields
        // (uom_code, uom_name, future ERP fields)
        // ============================================================
        setLines(data.lines || []);

      })
      .catch((err) => {
        console.error("Failed to load BOQ master", err);
        alert("Failed to load BOQ master");
        navigate("/ppe/engineering/boq");
      })
      .finally(() => setLoading(false));
  }, [boqId, navigate]);

  // ------------------------------------------------------------
  // SAVE DRAFT
  // ------------------------------------------------------------
  const saveDraft = async () => {
    if (mode === "view") return;

    if (!header.boq_name || !header.product_family_id) {
      alert("BOQ Name and Product Family are required");
      return;
    }

    try {
      setSaving(true);

      // ---------------------------------------------
      // CREATE
      // ---------------------------------------------
      if (!isEdit) {
        const res = await axiosClient.post(
          "/api/ppe/orders/boq-master",
          header
        );

        alert("BOQ master draft created");

        navigate(
          `/ppe/engineering/boq/edit/${res.data.data.id}`
        );
        return;
      }

      // ---------------------------------------------
      // UPDATE HEADER ONLY (lines handled separately)
      // ---------------------------------------------
      await axiosClient.put(
        `/api/ppe/orders/boq-master/${boqId}`,
        header
      );

      alert("BOQ master updated");

      navigate("/ppe/engineering/boq");
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.message ||
          "Failed to save BOQ master"
      );
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 10,
        left: 10,
        right: 0,
        bottom: 0,
        background: "#f8fafc",
        zIndex: 200,
        overflowY: "auto",
      }}
    >
      {/* ================= HEADER ================= */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background:
            "linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)",
          padding: "16px 20px",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>
            {mode === "view"
              ? "View BOQ Master"
              : isEdit
              ? "Edit BOQ Master"
              : "Create BOQ Master"}
          </h4>

          <div
            style={{
              fontSize: 13,
              opacity: 0.9,
            }}
          >
            Engineering Bill of Quantity definition
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-light"
            onClick={closeOverlay}
          >
            {mode === "view" ? "Close" : "Cancel"}
          </button>

          {mode !== "view" && (
            <button
              className="btn btn-dark"
              onClick={saveDraft}
              disabled={saving || loading}
            >
              {saving
                ? "Saving..."
                : isEdit
                ? "Update Draft"
                : "Save Draft"}
            </button>
          )}
        </div>
      </div>

      {/* ================= BODY ================= */}
      <div style={{ padding: 20 }}>
        <BOQHeaderForm
          value={header}
          onChange={setHeader}
          disabled={mode === "view"}
        />

        <BOQLineTable
          value={lines}
          onChange={setLines}
          readOnly={mode === "view"}
          boqId={boqId}
        />
      </div>
    </div>
  );
}
